import { useState, useRef, useEffect } from 'react';
import { Copy, Check, X, Mail, Share2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { track } from '../utils/analytics';

// Opens WhatsApp with pre-filled text.
// Mobile: whatsapp:// deep link → opens native app directly (no intermediate browser page).
// Desktop: falls back to wa.me web interface in a new tab.
export const openWhatsApp = (encodedText) => {
  const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  if (isMobile) {
    window.location.href = `whatsapp://send?text=${encodedText}`;
  } else {
    window.open(`https://wa.me/?text=${encodedText}`, '_blank', 'noopener,noreferrer');
  }
};

export const SendIcon = ({ size = 20 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 100 100"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ display: 'block', flexShrink: 0 }}
  >
    {/* bright cyan — upper face: left-tip → upper-right tip → fold notch */}
    <polygon points="5,42 85,8 42,58" fill="#00E5FF" />
    {/* darker blue — lower tail: upper-right tip → bottom tip → fold notch */}
    <polygon points="85,8 60,90 42,58" fill="#1AACD9" />
  </svg>
);

// Inline brand SVGs
const WhatsappIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="#25D366">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
    <path d="M12 0C5.373 0 0 5.373 0 12c0 2.136.559 4.14 1.535 5.876L.057 23.882a.5.5 0 00.614.612l6.111-1.604A11.945 11.945 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.882a9.866 9.866 0 01-5.017-1.371l-.36-.214-3.73.978.995-3.636-.235-.374A9.872 9.872 0 012.118 12C2.118 6.53 6.53 2.118 12 2.118S21.882 6.53 21.882 12 17.47 21.882 12 21.882z"/>
  </svg>
);

const TelegramIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="#229ED9">
    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12L7.26 13.825l-2.95-.924c-.641-.203-.654-.641.136-.951l11.5-4.432c.535-.194 1.002.131.948.703z"/>
  </svg>
);

// Try to fetch the activity image and return a File object for native sharing.
// Returns null if CORS blocks it or the URL is unavailable.

const ShareButton = ({ activityId, activityName, activitySlug, variant = 'card', cardContext = {} }) => {
  const { user } = useAuth();
  const [popover, setPopover] = useState(false);
  const [copied, setCopied] = useState(false);
  const wrapRef = useRef(null);

  const buildUrl = () => {
    const shareId = activitySlug || activityId;
    const ref = Math.random().toString(36).slice(2, 10); // random 8-char token for tracking
    return `${window.location.origin}/activities/${shareId}?ref=${ref}`;
  };

  // Text for native share (no URL — passed separately to avoid duplication)
  const buildShareTextNative = () =>
    `Let's make Bengaluru better together. Here's an activity on Karmana, you might be interested in\n\n*${activityName}*`;

  // Text for popover deep links (URL included since wa.me / mailto need it in the body)
  const buildShareTextWithUrl = (url) =>
    `Let's make Bengaluru better together. Here's an activity on Karmana, you might be interested in\n\n*${activityName}*\n${url}`;

  const shareTitle = `${activityName} – Karmana`;

  const handleShare = async () => {
    const shareMethod = navigator.share ? 'native' : 'copy';
    track('activity_share_click', { ...cardContext, activity_id: activityId, share_method: shareMethod });
    const url = buildUrl();

    if (navigator.share) {
      try {
        // text has no URL — url param adds it once, preventing duplication
        await navigator.share({ title: shareTitle, text: buildShareTextNative(), url });
        track('activity_share_success', { ...cardContext, activity_id: activityId, share_method: 'native' });
      } catch (err) {
        if (err.name === 'AbortError') {
          track('activity_share_cancelled', { ...cardContext, activity_id: activityId, share_method: 'native' });
        } else {
          setPopover(true);
        }
      }
    } else {
      setPopover(true);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(buildUrl());
      track('activity_share_copy', { ...cardContext, activity_id: activityId, share_method: 'copy' });
      setCopied(true);
      setTimeout(() => { setCopied(false); setPopover(false); }, 2000);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!popover) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setPopover(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [popover]);

  const url      = buildUrl();
  const fullMsg  = buildShareTextWithUrl(url);
  const encMsg   = encodeURIComponent(fullMsg);
  const encUrl   = encodeURIComponent(url);
  const encTitle = encodeURIComponent(shareTitle);
  const telegramText = encodeURIComponent(`Let's make Bengaluru better together. Here's an activity on Karmana, you might be interested in\n\n*${activityName}*`);

  // ── Icon-only variant: just the share icon, for use next to headings ──
  if (variant === 'icon-only') {
    return (
      <div className="relative" ref={wrapRef}>
        <button
          onClick={handleShare}
          data-testid={`share-btn-${activityId}`}
          title="Share"
          className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors active:scale-95"
        >
          <Share2 size={15} />
        </button>
        {popover && (
          <div
            className="absolute z-50 top-full mt-2 right-0 bg-white rounded-xl shadow-2xl border border-gray-200 p-3 w-52"
            data-testid="share-popover"
          >
            <div className="flex items-center justify-between mb-2.5">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Share via</p>
              <button onClick={() => setPopover(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                <X size={14} />
              </button>
            </div>
            <div className="space-y-1">
              <button onClick={copyLink} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-gray-50 text-sm text-gray-700 transition-colors" data-testid="share-copy-link">
                {copied ? <Check size={15} className="text-green-500 flex-shrink-0" /> : <Copy size={15} className="text-gray-500 flex-shrink-0" />}
                <span className={copied ? 'text-green-600 font-medium' : ''}>{copied ? 'Copied!' : 'Copy Link'}</span>
              </button>
              <button onClick={() => { openWhatsApp(encMsg); setPopover(false); }} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-gray-50 text-sm text-gray-700 transition-colors" data-testid="share-whatsapp">
                <WhatsappIcon /><span>WhatsApp</span>
              </button>
              <a href={`https://t.me/share/url?url=${encUrl}&text=${telegramText}`} target="_blank" rel="noopener noreferrer" onClick={() => setPopover(false)} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-gray-50 text-sm text-gray-700 transition-colors" data-testid="share-telegram">
                <TelegramIcon /><span>Telegram</span>
              </a>
              <a href={`mailto:?subject=${encTitle}&body=${encMsg}`} onClick={() => setPopover(false)} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-gray-50 text-sm text-gray-700 transition-colors" data-testid="share-email">
                <Mail size={15} className="text-gray-500 flex-shrink-0" /><span>Email</span>
              </a>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Activity footer variant: bare icon + label, no box — sits inside the 20% share slot
  if (variant === 'activity-share') {    return (
      <div className="relative h-full" ref={wrapRef}>
        <button
          onClick={handleShare}
          data-testid={`share-btn-${activityId}`}
          title="Share this activity"
          className="w-full h-full flex flex-col items-center justify-center gap-0.5 p-1.5 text-gray-400 hover:text-gray-600 active:scale-[0.97] transition-all duration-150"
        >
          <SendIcon size={17} />
          <span className="text-[9px] font-semibold leading-tight">Share</span>
        </button>
        {popover && (
          <div
            className="absolute z-50 bottom-full mb-2 left-1/2 -translate-x-1/2 bg-white rounded-xl shadow-2xl border border-gray-200 p-3 w-52"
            data-testid="share-popover"
          >
            <div className="flex items-center justify-between mb-2.5">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Share via</p>
              <button onClick={() => setPopover(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                <X size={14} />
              </button>
            </div>
            <div className="space-y-1">
              <button onClick={copyLink} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-gray-50 text-sm text-gray-700 transition-colors" data-testid="share-copy-link">
                {copied ? <Check size={15} className="text-green-500 flex-shrink-0" /> : <Copy size={15} className="text-gray-500 flex-shrink-0" />}
                <span className={copied ? 'text-green-600 font-medium' : ''}>{copied ? 'Copied!' : 'Copy Link'}</span>
              </button>
              <button onClick={() => { openWhatsApp(encMsg); setPopover(false); }} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-gray-50 text-sm text-gray-700 transition-colors" data-testid="share-whatsapp">
                <WhatsappIcon /><span>WhatsApp</span>
              </button>
              <a href={`https://t.me/share/url?url=${encUrl}&text=${telegramText}`} target="_blank" rel="noopener noreferrer" onClick={() => setPopover(false)} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-gray-50 text-sm text-gray-700 transition-colors" data-testid="share-telegram">
                <TelegramIcon /><span>Telegram</span>
              </a>
              <a href={`mailto:?subject=${encTitle}&body=${encMsg}`} onClick={() => setPopover(false)} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-gray-50 text-sm text-gray-700 transition-colors" data-testid="share-email">
                <Mail size={15} className="text-gray-500 flex-shrink-0" /><span>Email</span>
              </a>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Mobile footer variant: icon column, no button wrapper
  if (variant === 'mobile') {
    return (
      <div className="relative" ref={wrapRef}>
        <button
          onClick={handleShare}
          data-testid={`share-btn-${activityId}`}
          title="Share this activity"
          className="flex flex-col items-center gap-1"
        >
          <SendIcon size={22} />
          <span className="text-[10px] text-gray-500">Share</span>
        </button>
        {popover && (
          <div
            className="absolute z-50 bottom-full mb-2 left-1/2 -translate-x-1/2 bg-white rounded-xl shadow-2xl border border-gray-200 p-3 w-52"
            data-testid="share-popover"
          >
            <div className="flex items-center justify-between mb-2.5">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Share via</p>
              <button onClick={() => setPopover(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                <X size={14} />
              </button>
            </div>
            <div className="space-y-1">
              <button onClick={copyLink} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-gray-50 text-sm text-gray-700 transition-colors" data-testid="share-copy-link">
                {copied ? <Check size={15} className="text-green-500 flex-shrink-0" /> : <Copy size={15} className="text-gray-500 flex-shrink-0" />}
                <span className={copied ? 'text-green-600 font-medium' : ''}>{copied ? 'Copied!' : 'Copy Link'}</span>
              </button>
              <button onClick={() => { openWhatsApp(encMsg); setPopover(false); }} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-gray-50 text-sm text-gray-700 transition-colors" data-testid="share-whatsapp">
                <WhatsappIcon /><span>WhatsApp</span>
              </button>
              <a href={`https://t.me/share/url?url=${encUrl}&text=${telegramText}`} target="_blank" rel="noopener noreferrer" onClick={() => setPopover(false)} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-gray-50 text-sm text-gray-700 transition-colors" data-testid="share-telegram">
                <TelegramIcon /><span>Telegram</span>
              </a>
              <a href={`mailto:?subject=${encTitle}&body=${encMsg}`} onClick={() => setPopover(false)} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-gray-50 text-sm text-gray-700 transition-colors" data-testid="share-email">
                <Mail size={15} className="text-gray-500 flex-shrink-0" /><span>Email</span>
              </a>
            </div>
          </div>
        )}
      </div>
    );
  }

  const isCard = variant === 'card';
  const btnCls = isCard
    ? 'w-full flex items-center justify-center gap-2 px-3 py-3 rounded-lg border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors text-sm font-medium whitespace-nowrap'
    : 'w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors text-sm font-medium whitespace-nowrap';

  return (
    <div className="relative" ref={wrapRef}>
      <button
        onClick={handleShare}
        className={btnCls}
        data-testid={`share-btn-${activityId}`}
        title="Share this activity"
      >
        <SendIcon size={30} />
        <span>Share</span>
      </button>

      {popover && (
        <div
          className="absolute z-50 bottom-full mb-2 right-0 bg-white rounded-xl shadow-2xl border border-gray-200 p-3 w-52"
          data-testid="share-popover"
        >
          <div className="flex items-center justify-between mb-2.5">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Share via</p>
            <button onClick={() => setPopover(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
              <X size={14} />
            </button>
          </div>
          <div className="space-y-1">
            <button
              onClick={copyLink}
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-gray-50 text-sm text-gray-700 transition-colors"
              data-testid="share-copy-link"
            >
              {copied
                ? <Check size={15} className="text-green-500 flex-shrink-0" />
                : <Copy size={15} className="text-gray-500 flex-shrink-0" />}
              <span className={copied ? 'text-green-600 font-medium' : ''}>{copied ? 'Copied!' : 'Copy Link'}</span>
            </button>
            <button
              onClick={() => { openWhatsApp(encMsg); setPopover(false); }}
              rel="noopener noreferrer"
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-gray-50 text-sm text-gray-700 transition-colors"
              data-testid="share-whatsapp"
            >
              <WhatsappIcon />
              <span>WhatsApp</span>
            </button>
            <a
              href={`https://t.me/share/url?url=${encUrl}&text=${telegramText}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setPopover(false)}
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-gray-50 text-sm text-gray-700 transition-colors"
              data-testid="share-telegram"
            >
              <TelegramIcon />
              <span>Telegram</span>
            </a>
            <a
              href={`mailto:?subject=${encTitle}&body=${encMsg}`}
              onClick={() => setPopover(false)}
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-gray-50 text-sm text-gray-700 transition-colors"
              data-testid="share-email"
            >
              <Mail size={15} className="text-gray-500 flex-shrink-0" />
              <span>Email</span>
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShareButton;
